import { useState, useEffect, useCallback } from 'react';
import {
  ChatBubbleLeftIcon,
  GlobeAltIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { API_ENDPOINTS } from '../../config/constants';

interface Comment {
  id: string;
  author_name: string;
  author_website?: string;
  author_photo?: string;
  content: string;
  created_at: string;
  parent_id?: string;
}

interface CommentsListProps {
  postId: string;
  refreshTrigger?: number;
}

interface CommentsResponse {
  success: boolean;
  data: {
    comments: Comment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

const CommentsList = ({ postId, refreshTrigger }: CommentsListProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalComments, setTotalComments] = useState(0);

  const fetchComments = useCallback(
    async (pageNum: number = 1) => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_ENDPOINTS.comments}/post/${postId}?page=${pageNum}&limit=20`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch comments');
        }

        const result: CommentsResponse = await response.json();

        if (result.success) {
          if (pageNum === 1) {
            setComments(result.data.comments);
          } else {
            setComments(prev => [...prev, ...result.data.comments]);
          }

          setPage(pageNum);
          setTotalComments(result.data.pagination.total);
          setHasMore(pageNum < result.data.pagination.totalPages);
        } else {
          throw new Error('Failed to load comments');
        }
      } catch (err) {
        console.error('Fetch comments error:', err);
        setError('Failed to load comments');
      } finally {
        setLoading(false);
      }
    },
    [postId]
  );

  useEffect(() => {
    fetchComments(1);
  }, [postId, refreshTrigger, fetchComments]);

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchComments(page + 1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatContent = (content: string) => {
    // Basic content formatting - convert line breaks to paragraphs
    return content.split('\n\n').map((paragraph, index) => (
      <p key={index} className="mb-2 last:mb-0">
        {paragraph.split('\n').map((line, lineIndex) => (
          <span key={lineIndex}>
            {line}
            {lineIndex < paragraph.split('\n').length - 1 && <br />}
          </span>
        ))}
      </p>
    ));
  };

  if (loading && comments.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="border-b border-gray-100 pb-4">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-center text-gray-500">
          <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          Comments ({totalComments})
        </h3>
        <ChatBubbleLeftIcon className="h-6 w-6 text-gray-400" />
      </div>

      {comments.length === 0 ? (
        <div className="text-center py-8">
          <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">
            No comments yet. Be the first to share your thoughts!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {comments.map((comment, index) => (
            <div
              key={comment.id}
              className={`${
                index !== comments.length - 1
                  ? 'border-b border-gray-100 pb-6'
                  : ''
              }`}
            >
              {/* Comment Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {/* Avatar */}
                  {comment.author_photo ? (
                    <img
                      src={comment.author_photo}
                      alt={comment.author_name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 bg-slate-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                      {comment.author_name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Author Info */}
                  <div>
                    <div className="flex items-center space-x-2">
                      {comment.author_website ? (
                        <a
                          href={comment.author_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {comment.author_name}
                        </a>
                      ) : (
                        <span className="font-medium text-gray-900">
                          {comment.author_name}
                        </span>
                      )}

                      {comment.author_website && (
                        <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </div>

                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <CalendarIcon className="h-3 w-3" />
                      <span>{formatDate(comment.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comment Content */}
              <div className="prose prose-sm max-w-none text-gray-700 ml-13">
                {formatContent(comment.content)}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center pt-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>Load More Comments</>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentsList;
